// Layered network-flow model for the Overview graph section. Assembles a
// left→right flow of four columns from the REST resources:
//
//   Networks —(serving)→ Clients×Network —(serving)→ Services —(exposes)→ Tools
//
// H38 — the leftmost column is NETWORKS (Clients/Networks were swapped), and a client
// that is in several networks is modelled as a SEPARATE node per network (client×network),
// because each network may carry its own service settings. The graph data model is keyed
// on (client, network) so those per-network rows are first-class nodes.
//
// All relationships are DERIVED (nothing hard-coded):
//   • network → client : the client's provider instances resolve to a publication,
//                          whose `network_id` places the client in that network. One
//                          client×network node per distinct network the client serves.
//   • client → service : the client×network node links to the publications it actually
//                          provides in that network (instance → publication).
//   • service → tool   : the publication's exposed tools (from the service def),
//                          named canonically `network__service__tool`.
//
// The assembly + the filter/collapse helpers are PURE so they can be unit-tested
// without the API or the DOM (see networkGraph.test.ts).

import type {
  Client,
  Network,
  ProviderInstance,
  ServiceDefinition,
  ServicePublication,
} from '../api/schemas';

export type Column = 'clients' | 'networks' | 'services' | 'tools';

export type NodeTone = 'ok' | 'warn' | 'bad' | 'info' | 'muted';

export interface FlowNode {
  id: string;
  column: Column;
  label: string;
  sublabel?: string;
  tone?: NodeTone;
  // H37 — drill-in metadata: which underlying entity a node maps to, so the UI can resolve
  // its task-queue rows. `canonical` is the canonical tool name (tool nodes); `clientId` /
  // `networkId` / `pubId` identify the other columns.
  canonical?: string;
  clientId?: string;
  networkId?: string;
  pubId?: string;
  // True when the entity is published/known but NOT currently backed by a live provider (a service/tool
  // with no advertising node, or a client serving nothing). The UI renders these in a disabled style so
  // it's obvious WHAT exists-but-isn't-running, not just what's live.
  disabled?: boolean;
}

export interface FlowEdge {
  from: string;
  to: string;
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface GraphInput {
  clients: Client[];
  networks: Network[];
  services: ServiceDefinition[];
  publications: ServicePublication[];
  instances: ProviderInstance[];
}

// Column order (left → right) plus the relationship label INTO each column from
// the one before it. The UI renders these as the column headers + connector labels.
// H38 — Networks is now the leftmost (root) column; Clients (per network) follow it.
export const COLUMNS: { key: Column; title: string; rel?: string }[] = [
  { key: 'networks', title: 'Networks' },
  { key: 'clients', title: 'Clients', rel: 'serving' },
  { key: 'services', title: 'Services', rel: 'serving' },
  { key: 'tools', title: 'Tools', rel: 'exposes' },
];

export const COLUMN_ORDER: Column[] = ['networks', 'clients', 'services', 'tools'];

/** The root (always-visible) column — the leftmost in COLUMN_ORDER. */
export const ROOT_COLUMN: Column = COLUMN_ORDER[0];

const clientTone = (s: Client['status']): NodeTone =>
  s === 'online' ? 'ok' : s === 'draining' ? 'warn' : 'bad';

/** Graph node sublabel: "<status> · host=<host> · <free>/<total> GB". */
function clientSublabel(c: Client): string {
  const np = c.node_profile;
  const parts: string[] = [c.status];
  const host = np.labels?.host ?? np.hostname;
  if (host) parts.push(`host=${host}`);
  const gpus = np.gpus ?? [];
  if (gpus.length) {
    const total = gpus.reduce((m, g) => m + g.vram_total_mb, 0);
    const free = gpus.reduce((m, g) => m + g.vram_free_mb, 0);
    const gb = (mb: number) => Math.round(mb / 1024);
    parts.push(`${gpus.length}×GPU ${gb(free)}/${gb(total)} GB`);
  }
  return parts.join(' · ');
}
const pubTone = (s: ServicePublication['status']): NodeTone =>
  s === 'approved' ? 'ok' : s === 'pending' ? 'warn' : 'bad';

/**
 * Per-network client node id. A client that serves several networks gets one node per network
 * (`cli:<client>@<network>`); a client serving no network keeps a single bare node (`cli:<client>`)
 * so idle/unconnected nodes still appear in the Clients column.
 */
export function clientNodeId(clientId: string, networkId?: string): string {
  return networkId ? `cli:${clientId}@${networkId}` : `cli:${clientId}`;
}

/**
 * The tools a publication actually exposes — the gateway's rule, not a literal read of the array.
 *
 * `exposed_tool_ids` is a PIN, and the server has always treated it as an optional narrowing:
 * a NON-EMPTY list restricts the publication to that subset, an EMPTY list exposes ALL of the
 * service definition's tools. Both `/api/v1/tools` and the `/mcp` tools/list build their catalogs
 * that way. The graph did not: it filtered with `includes()` on the raw array, and `[].includes(x)`
 * is false for every x — so a publication that pins nothing drew as a service with no tools under it.
 *
 * Live on cubi (2026-09-05): a user published their own node's `unreal-engine-mcp-server` (23 tools)
 * and `playwright` (23 tools) into their private network. `GET /tools` listed all 46 under their own
 * identity; the Overview graph showed two service cards with nothing beneath them, and the user
 * reported the platform had swallowed their tools. Nothing was hidden — the panel was reading the
 * pin backwards.
 *
 * The stale-pin fallback is the server's too (`_tool_catalog`): tool rows can be re-created with
 * fresh ids, and a pin that names no CURRENT tool of its own service is a dead binding, not an
 * instruction to hide the whole service. Matching on id-or-name is what the gateway does.
 */
export function exposedTools<T extends { id: string; name: string }>(
  tools: readonly T[],
  pinned: readonly string[] | null | undefined,
): T[] {
  const pins = new Set(pinned ?? []);
  if (pins.size === 0) return [...tools];
  const kept = tools.filter((t) => pins.has(t.id) || pins.has(t.name));
  return kept.length > 0 ? kept : [...tools];
}

/** Join the REST resources into the layered Networks→Clients×Network→Services→Tools flow. */
export function assembleGraph(input: GraphInput): FlowGraph {
  const { clients, networks, services, publications, instances } = input;
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const edgeSeen = new Set<string>();
  const addEdge = (from: string, to: string) => {
    const k = `${from}->${to}`;
    if (edgeSeen.has(k)) return;
    edgeSeen.add(k);
    edges.push({ from, to });
  };

  const netById = new Map(networks.map((n) => [n.id, n]));
  const defById = new Map(services.map((s) => [s.id, s]));
  const pubById = new Map(publications.map((p) => [p.id, p]));
  const clientById = new Map(clients.map((c) => [c.id, c]));

  // Column 1 — Networks (the roots).
  for (const n of networks) {
    nodes.push({
      id: `net:${n.id}`,
      column: 'networks',
      label: n.name,
      sublabel: `/${n.slug}`,
      tone: n.status === 'active' ? 'ok' : 'bad',
      networkId: n.id,
    });
  }

  // Derive each client's set of networks from its provider instances (instance → publication →
  // network). H38: this drives the client×network expansion — one Clients-column node per
  // (client, network) pair the client actually serves.
  const clientNets = new Map<string, Set<string>>(); // clientId -> networkIds
  const livePubIds = new Set<string>(); // publications with ≥1 live provider instance
  for (const inst of instances) {
    if (!inst.service_publication_id) continue; // synthetic (idle-stopped) rows carry no publication
    const pub = pubById.get(inst.service_publication_id);
    if (!pub || !netById.has(pub.network_id)) continue;
    livePubIds.add(pub.id);
    if (!clientById.has(inst.client_id)) continue;
    let set = clientNets.get(inst.client_id);
    if (!set) clientNets.set(inst.client_id, (set = new Set()));
    set.add(pub.network_id);
  }

  // Column 2 — Clients, one node per (client, network). A client with no resolved network still
  // appears once as a bare node so idle nodes are visible. Sublabel leads with the network slug.
  const clientNodeSeen = new Set<string>();
  const addClientNode = (c: Client, networkId?: string) => {
    const id = clientNodeId(c.id, networkId);
    if (clientNodeSeen.has(id)) return id;
    clientNodeSeen.add(id);
    const net = networkId ? netById.get(networkId) : undefined;
    const sub = net ? `/${net.slug} · ${clientSublabel(c)}` : clientSublabel(c);
    nodes.push({
      id,
      column: 'clients',
      label: c.name,
      sublabel: sub,
      tone: clientTone(c.status),
      clientId: c.id,
      networkId,
      // A bare client (no resolved network) is serving nothing → disabled; an offline client too.
      disabled: networkId === undefined || c.status === 'offline',
    });
    return id;
  };
  for (const c of clients) {
    const nets = clientNets.get(c.id);
    if (!nets || nets.size === 0) {
      addClientNode(c); // bare node — no network membership resolved
      continue;
    }
    for (const netId of nets) {
      const cid = addClientNode(c, netId);
      // network —serving→ client×network
      addEdge(`net:${netId}`, cid);
    }
  }

  // Column 3 + 4 — Services (one per publication) and their exposed Tools.
  for (const pub of publications) {
    const def = defById.get(pub.service_definition_id);
    const net = netById.get(pub.network_id);
    const svcId = `svc:${pub.id}`;
    const pubLive = livePubIds.has(pub.id);
    nodes.push({
      id: svcId,
      column: 'services',
      label: pub.service_slug,
      sublabel: def?.name ?? pub.service_name,
      tone: pubTone(pub.status),
      pubId: pub.id,
      networkId: pub.network_id,
      // Published but no live provider → disabled (the agent could request it, but it isn't running).
      disabled: !pubLive,
    });

    const slug = net?.slug ?? pub.network_slug ?? 'net';
    const exposed = exposedTools(def?.tools ?? [], pub.exposed_tool_ids);
    for (const t of exposed) {
      const canonical = `${slug}__${pub.service_slug}__${t.name}`;
      const toolId = `tool:${pub.id}:${t.name}`;
      nodes.push({
        id: toolId,
        column: 'tools',
        label: t.name,
        sublabel: canonical,
        tone: t.streaming ? 'info' : 'ok',
        canonical,
        pubId: pub.id,
        networkId: pub.network_id,
        disabled: !pubLive,
      });
      // service —exposes→ tool
      addEdge(svcId, toolId);
    }
  }

  // Client×Network —serving→ Service, derived from provider instances: the client serves the
  // publication's tools within that publication's network.
  for (const inst of instances) {
    if (!inst.service_publication_id) continue; // synthetic (idle-stopped) rows carry no publication
    const pub = pubById.get(inst.service_publication_id);
    if (!pub || !netById.has(pub.network_id)) continue;
    const cid = clientNodeId(inst.client_id, pub.network_id);
    if (!clientNodeSeen.has(cid)) continue;
    addEdge(cid, `svc:${pub.id}`);
  }

  return { nodes, edges };
}

interface Adjacency {
  parents: Map<string, string[]>;
  children: Map<string, string[]>;
}

function adjacency(graph: FlowGraph): Adjacency {
  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  const push = (m: Map<string, string[]>, k: string, v: string) => {
    const arr = m.get(k);
    if (arr) arr.push(v);
    else m.set(k, [v]);
  };
  for (const e of graph.edges) {
    push(parents, e.to, e.from);
    push(children, e.from, e.to);
  }
  return { parents, children };
}

/**
 * Visibility under per-node collapse. The ROOT column's nodes are always visible. A
 * downstream node is visible iff it is an orphan (no incoming edges — show it
 * standalone) OR at least one of its parents is itself visible AND not collapsed.
 * Collapsing an upstream node therefore hides exactly the downstream nodes reached
 * ONLY through it (a sibling, still-expanded parent keeps a shared child visible).
 */
export function visibleNodeIds(graph: FlowGraph, collapsed: ReadonlySet<string>): Set<string> {
  const { parents } = adjacency(graph);
  const visible = new Set<string>();
  for (const col of COLUMN_ORDER) {
    for (const n of graph.nodes) {
      if (n.column !== col) continue;
      if (col === ROOT_COLUMN) {
        visible.add(n.id);
        continue;
      }
      const ps = parents.get(n.id);
      if (!ps || ps.length === 0) {
        visible.add(n.id); // orphan — no upstream gate
        continue;
      }
      if (ps.some((p) => visible.has(p) && !collapsed.has(p))) visible.add(n.id);
    }
  }
  return visible;
}

/**
 * Nodes to KEEP for a search query: every node whose label/sublabel matches, plus
 * its full connected chain (all upstream ancestors and downstream descendants), so
 * a match never appears disconnected. Returns `null` when the query is empty (no
 * filtering — keep everything).
 */
export function matchingChain(graph: FlowGraph, query: string): Set<string> | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const { parents, children } = adjacency(graph);
  const keep = new Set<string>();

  const walk = (id: string, m: Map<string, string[]>) => {
    for (const next of m.get(id) ?? []) {
      if (!keep.has(next)) {
        keep.add(next);
        walk(next, m);
      }
    }
  };

  for (const n of graph.nodes) {
    const hit =
      n.label.toLowerCase().includes(q) || (n.sublabel?.toLowerCase().includes(q) ?? false);
    if (!hit) continue;
    keep.add(n.id);
    walk(n.id, parents);
    walk(n.id, children);
  }
  return keep;
}

/**
 * Order each column to minimize edge crossings and visually GROUP connected nodes — a network's whole
 * subtree (its clients → their services → their tools) clusters together instead of being scattered by
 * a column-wide alphabetical sort (which made the bezier connectors cross all over and read as noise).
 *
 * Layered-graph barycenter heuristic, one left→right pass: the root column (Networks) is sorted by
 * label; every subsequent column is ordered by the mean index ("barycenter") of each node's parents in
 * the already-ordered previous column, tie-broken by label. Nodes with no parents (orphans — e.g. a
 * bare client with no resolved network) sort to the end of their column by label. PURE — unit-tested.
 */
export function orderColumns(graph: FlowGraph): Map<Column, FlowNode[]> {
  const { parents } = adjacency(graph);
  const byCol = new Map<Column, FlowNode[]>();
  for (const col of COLUMN_ORDER) byCol.set(col, []);
  for (const n of graph.nodes) byCol.get(n.column)?.push(n);

  // Position of each node within its (ordered) column — feeds the next column's barycenters.
  const indexOf = new Map<string, number>();
  const ORPHAN = Number.POSITIVE_INFINITY; // no parents → cluster at the column's end

  COLUMN_ORDER.forEach((col, ci) => {
    const list = byCol.get(col);
    if (!list) return;
    if (ci === 0) {
      list.sort((a, b) => a.label.localeCompare(b.label));
    } else {
      const bary = (n: FlowNode): number => {
        const ps = parents.get(n.id);
        if (!ps || ps.length === 0) return ORPHAN;
        let sum = 0;
        let cnt = 0;
        for (const p of ps) {
          const idx = indexOf.get(p);
          if (idx != null) {
            sum += idx;
            cnt += 1;
          }
        }
        return cnt ? sum / cnt : ORPHAN;
      };
      list.sort((a, b) => {
        const da = bary(a);
        const db = bary(b);
        // Infinity===Infinity (both orphans) falls through to the label tiebreak — never NaN.
        if (da !== db) return da - db;
        return a.label.localeCompare(b.label);
      });
    }
    list.forEach((n, i) => indexOf.set(n.id, i));
  });
  return byCol;
}

/** True for nodes that have downstream children (so the UI shows a collapse toggle). */
export function nodesWithChildren(graph: FlowGraph): Set<string> {
  const out = new Set<string>();
  for (const e of graph.edges) out.add(e.from);
  return out;
}

// --- routes overlay (H7) ---------------------------------------------------
// The routes layer draws the network stream routes (ADR-020) OVER the graph: a directed segment
// between the tool nodes of consecutive hops, styled by how much the route has been USED.

export interface RouteUsageCountersLike {
  instantiations: number;
  completions: number;
  errors: number;
  active: number;
  last_used_ms?: number | null;
}

export interface RouteUsageLike {
  route_id: string;
  route_name: string;
  network_id: string;
  version: number;
  hops: string[]; // canonical tools
  edges: { from_tool: string; to_tool: string }[];
  usage: RouteUsageCountersLike;
}

export interface RouteOverlayEdge {
  key: string;
  routeName: string;
  fromId: string; // tool node id
  toId: string; // tool node id
  used: boolean; // any exercise recorded
  active: boolean; // an instance currently running
  instantiations: number;
}

export interface RouteOverlay {
  edges: RouteOverlayEdge[];
  /** Tool node ids touched by a USED route (for node highlighting incl. single-hop routes). */
  usedNodeIds: Set<string>;
  maxInstantiations: number;
  routeCount: number;
  usedRouteCount: number;
}

/**
 * Build the routes overlay from the route-usage payloads + the assembled graph. A route edge is
 * mapped onto the graph by matching each hop's canonical tool name to the tool node whose `sublabel`
 * carries that canonical name; edges whose endpoints aren't present in the (filtered) graph are
 * dropped so the overlay never points at hidden nodes. PURE — unit-tested without the DOM.
 */
export function buildRouteOverlay(
  graph: FlowGraph,
  routes: RouteUsageLike[],
  visibleIds?: ReadonlySet<string>,
): RouteOverlay {
  // canonical tool name -> tool node id (sublabel is the canonical name for tool nodes).
  const canonToId = new Map<string, string>();
  for (const n of graph.nodes) {
    if (n.column === 'tools' && n.sublabel) canonToId.set(n.sublabel, n.id);
  }
  const present = (id: string | undefined): id is string =>
    !!id && (!visibleIds || visibleIds.has(id));

  const edges: RouteOverlayEdge[] = [];
  const usedNodeIds = new Set<string>();
  let maxInstantiations = 0;
  let usedRouteCount = 0;

  for (const r of routes) {
    const used = (r.usage?.instantiations ?? 0) > 0;
    const active = (r.usage?.active ?? 0) > 0;
    if (used) usedRouteCount += 1;
    maxInstantiations = Math.max(maxInstantiations, r.usage?.instantiations ?? 0);

    // highlight every hop tool node of a used route (covers single-hop routes with no edge).
    if (used) {
      for (const canon of r.hops) {
        const id = canonToId.get(canon);
        if (present(id)) usedNodeIds.add(id);
      }
    }

    for (let i = 0; i < r.edges.length; i++) {
      const e = r.edges[i];
      const fromId = canonToId.get(e.from_tool);
      const toId = canonToId.get(e.to_tool);
      if (!present(fromId) || !present(toId)) continue;
      edges.push({
        key: `${r.network_id}:${r.route_name}:${i}`,
        routeName: r.route_name,
        fromId,
        toId,
        used,
        active,
        instantiations: r.usage?.instantiations ?? 0,
      });
    }
  }

  return {
    edges,
    usedNodeIds,
    maxInstantiations,
    routeCount: routes.length,
    usedRouteCount,
  };
}

// --- task-queue overlay (H37) ----------------------------------------------
// The graph annotates nodes with task-queue depth (queued) + in-flight (running). The gateway
// exposes per-canonical-tool queues (GET /v1/queues → QueueDepth) and a per-node total
// (Client.queue_depth). We map per-tool rows onto tool nodes and aggregate UP the graph
// (service = its tools; client×network / network = all reachable tools) so every column shows a
// queue figure, with a drill-in listing the contributing per-tool rows.

export interface QueueDepthLike {
  canonical_tool: string;
  depth: number;
  running: number;
  providers: number;
  oldest_wait_s?: number;
}

export interface NodeQueueSummary {
  queued: number; // total queued (depth) across contributing tools
  running: number; // total in-flight across contributing tools
  rows: QueueDepthLike[]; // contributing per-tool queue rows (for the drill-in)
  nodeTotal?: number; // a per-node total (Client.queue_depth) when the node is a client
}

// The live (event-bus, queue.changed) per-tool queue shape — leaner than the REST QueueDepth: it
// carries depth + wait but NOT running/providers.
export interface LiveQueueLike {
  canonical_tool: string;
  depth: number;
  oldest_wait_ms?: number;
}

/**
 * Overlay live per-tool queue depth (event bus, queue.changed) onto the REST seed rows so depth +
 * oldest-wait update INSTANTLY without a refetch. The REST rows carry fields the bus omits (running,
 * providers) — those are kept from the seed; live-only tools (not in the seed yet) are appended with
 * running/providers defaulted to 0. PURE.
 */
export function mergeQueues(
  seed: QueueDepthLike[],
  live: Record<string, LiveQueueLike>,
): QueueDepthLike[] {
  const out = seed.map((q) => {
    const lq = live[q.canonical_tool];
    if (!lq) return q;
    return {
      ...q,
      depth: lq.depth,
      oldest_wait_s:
        lq.oldest_wait_ms != null ? Math.round(lq.oldest_wait_ms / 1000) : q.oldest_wait_s,
    };
  });
  const seen = new Set(seed.map((q) => q.canonical_tool));
  for (const lq of Object.values(live)) {
    if (seen.has(lq.canonical_tool)) continue;
    out.push({
      canonical_tool: lq.canonical_tool,
      depth: lq.depth,
      running: 0,
      providers: 0,
      oldest_wait_s: lq.oldest_wait_ms != null ? Math.round(lq.oldest_wait_ms / 1000) : undefined,
    });
  }
  return out;
}

/**
 * Tool canonical names reachable downstream from `nodeId` (the node's own canonical for a tool
 * node). PURE — used to aggregate per-tool queues up to service / client / network nodes.
 */
export function descendantToolCanonicals(graph: FlowGraph, nodeId: string): string[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const start = byId.get(nodeId);
  if (!start) return [];
  if (start.column === 'tools') return start.canonical ? [start.canonical] : [];
  const { children } = adjacency(graph);
  const out = new Set<string>();
  const seen = new Set<string>();
  const walk = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    const node = byId.get(id);
    if (node?.column === 'tools' && node.canonical) out.add(node.canonical);
    for (const c of children.get(id) ?? []) walk(c);
  };
  walk(nodeId);
  return [...out];
}

// --- realtime activity overlay ---------------------------------------------
// Live tasks (event bus) projected onto the graph so the user SEES calls happening: a tool node with an
// in-flight task pulses; one with a recent failure is flagged. Aggregated UP the graph (service = its
// tools; client×network / network = all reachable tools) so activity shows at every column.

export interface ToolActivity {
  running: number; // in-flight tasks (queued/dispatched/running)
  failed: number; // recent failures (failed/rejected/lost within the recency window)
}

export interface NodeActivity {
  active: boolean; // ≥1 in-flight task reachable from this node
  error: boolean; // ≥1 recent failure reachable from this node
  running: number;
  failed: number;
}

/**
 * Per-node realtime activity from a canonical-tool → {running, failed} index. PURE — the caller builds
 * the index from the live task store (classifying task state + applying a recency window for failures).
 */
export function activityByNode(
  graph: FlowGraph,
  byCanon: Map<string, ToolActivity>,
): Map<string, NodeActivity> {
  const out = new Map<string, NodeActivity>();
  if (byCanon.size === 0) return out;
  for (const n of graph.nodes) {
    let running = 0;
    let failed = 0;
    for (const c of descendantToolCanonicals(graph, n.id)) {
      const a = byCanon.get(c);
      if (a) {
        running += a.running;
        failed += a.failed;
      }
    }
    if (running > 0 || failed > 0) {
      out.set(n.id, { active: running > 0, error: failed > 0, running, failed });
    }
  }
  return out;
}

/** Build a canonical-tool → queue-row index. */
export function queueIndex(queues: QueueDepthLike[]): Map<string, QueueDepthLike> {
  const m = new Map<string, QueueDepthLike>();
  for (const q of queues) m.set(q.canonical_tool, q);
  return m;
}

/**
 * Summarize the task queues attributable to a node: the per-tool rows for the tools it reaches,
 * summed into queued/running totals. For a client node, `clientQueueDepth` supplies the per-node
 * total reported by the gateway (Client.queue_depth). PURE.
 */
export function summarizeNodeQueue(
  graph: FlowGraph,
  nodeId: string,
  index: Map<string, QueueDepthLike>,
  clientQueueDepth?: Map<string, number>,
): NodeQueueSummary {
  const node = graph.nodes.find((n) => n.id === nodeId);
  const canonicals = descendantToolCanonicals(graph, nodeId);
  const rows: QueueDepthLike[] = [];
  let queued = 0;
  let running = 0;
  for (const c of canonicals) {
    const row = index.get(c);
    if (!row) continue;
    rows.push(row);
    queued += row.depth;
    running += row.running;
  }
  const nodeTotal =
    node?.column === 'clients' && node.clientId ? clientQueueDepth?.get(node.clientId) : undefined;
  return { queued, running, rows, nodeTotal };
}


// --- node health filter (UX1) ----------------------------------------------
//
// The graph could say a node was in trouble — a red flag from a recent failure, a
// dimmed card for a service with no live provider — but it could not ANSWER the
// question those marks raise: "show me the ones that are broken". On a fleet of a
// few hundred nodes, hunting for a red border by scrolling is not an answer.
//
// So health is a first-class filter, next to the name search. Same discipline as the
// search: the classification is PURE and lives here, and the filter keeps a matching
// node's whole connected CHAIN, because a failing tool with its service, node and
// network stripped away is a name with no context — the picture has to stay a graph.

export type NodeHealth = 'healthy' | 'failed' | 'offline';

/** What a card is saying about itself, in one word.
 *
 * `failed` is a REAL failure — a recent errored task reachable from this node, or a
 * node the assembler already toned as bad (an offline client, a rejected
 * publication). `offline` is the other thing a dimmed card means: known and
 * publishable, but with nothing live serving it right now. The two are kept apart
 * because "nobody is running this" and "this broke" are different mornings. */
export function nodeHealth(node: FlowNode, activity?: NodeActivity): NodeHealth {
  if (activity?.error || node.tone === 'bad') return 'failed';
  if (node.disabled) return 'offline';
  return 'healthy';
}

/** The health chips, in the order they are drawn. `all` is the off position. */
export const HEALTH_FILTERS = ['all', 'healthy', 'failed', 'offline'] as const;
export type HealthFilter = (typeof HEALTH_FILTERS)[number];

/**
 * The nodes a health filter leaves visible, chains included — or null for "all",
 * which is the same "no opinion" signal `matchingChain` returns for an empty query,
 * so the two filters compose by plain set intersection at the call site.
 */
export function healthChain(
  graph: FlowGraph,
  filter: HealthFilter,
  activity: Map<string, NodeActivity>,
): Set<string> | null {
  if (filter === 'all') return null;
  const { parents, children } = adjacency(graph);
  const keep = new Set<string>();

  const walk = (id: string, m: Map<string, string[]>) => {
    for (const next of m.get(id) ?? []) {
      if (!keep.has(next)) {
        keep.add(next);
        walk(next, m);
      }
    }
  };

  for (const n of graph.nodes) {
    if (nodeHealth(n, activity.get(n.id)) !== filter) continue;
    keep.add(n.id);
    walk(n.id, parents);
    walk(n.id, children);
  }
  return keep;
}

/** How many nodes are in each health state — the counts drawn on the chips. */
export function healthCounts(
  graph: FlowGraph,
  activity: Map<string, NodeActivity>,
): Record<NodeHealth, number> {
  const out: Record<NodeHealth, number> = { healthy: 0, failed: 0, offline: 0 };
  for (const n of graph.nodes) out[nodeHealth(n, activity.get(n.id))] += 1;
  return out;
}

// --- scope (UX18) -----------------------------------------------------------

/**
 * Narrow a graph input to a set of networks, BEFORE assembly.
 *
 * Filtering the assembled graph would leave the edges of dropped networks pointing
 * at nodes that are no longer there; filtering the input leaves a smaller but whole
 * topology. The cut cascades the way the data hangs together: publications belong to
 * a network, provider instances to a publication, and a client appears in the graph
 * only through an instance.
 *
 * A client with no instance in any surviving network is dropped rather than kept as
 * a bare node: the panel is answering "these networks", and a node in none of them
 * is not part of that answer. It is still on the Nodes page, which is the page about
 * nodes.
 */
export function restrictInput(input: GraphInput, networkIds: ReadonlySet<string>): GraphInput {
  const networks = input.networks.filter((n) => networkIds.has(n.id));
  const publications = input.publications.filter((p) => networkIds.has(p.network_id));
  const pubIds = new Set(publications.map((p) => p.id));
  // `service_publication_id` is optional on the wire (an instance can be pending a
  // publication); one without it belongs to no network, so it cannot survive a
  // network cut.
  const instances = input.instances.filter(
    (i) => i.service_publication_id != null && pubIds.has(i.service_publication_id),
  );
  const clientIds = new Set(instances.map((i) => i.client_id));
  return {
    networks,
    publications,
    instances,
    clients: input.clients.filter((c) => clientIds.has(c.id)),
    // Service definitions are org-wide, not per-network; the assembler only draws the
    // ones a surviving publication points at, so they are passed through untouched.
    services: input.services,
  };
}
